import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useLocation} from "react-router-dom";
import {
    ActivityItem,
    getInitials,
    Icon,
    IconButton,
    MessageBar,
    MessageBarButton,
    MessageBarType,
    ProgressIndicator,
    SharedColors,
    Stack,
    Text,
} from "@fluentui/react";
import Axios from "axios";
import {UserInfoHelper} from "../helpers/UserInfoHelper";
import {LanguageContext} from "../contexts/LanguageContext";
import {AuthContext} from "../contexts/AuthContext";
import {WakaTimeWorklogSourceType} from "../API";
import {OnixCallout} from "../components/OnixCallout";
import {MakeHub} from "../MakeHub";
import {CurrentSupportTicketContext} from "../contexts/CurrentSupportTicketContext";
import {ApplicationPacketEnum} from "../graphql-types";
import {
    parseTimeLogDurationToMinutes,
    roundTimeLogDurationUpToQuarterHour,
    suggestTimeLogDuration,
    userTimeLogRoundingMinutes,
} from "../helpers/TimeLogDurationHelper";
import {
    getRouteScopedCurrentTicket,
    isSupportActivityRelatedToCurrentTicket,
    isSupportActivityVisibleForTicket,
    isCurrentSupportTicketRoute,
    supportActivityCandidateLabel,
} from "../helpers/SupportTicketRouteHelper";
import "../styles/TeamViewerMessageBar.css";
import TeamViewerMessageBarContext from "../contexts/TeamViewerMessageBarContext";
import {ActivityTimeLogForm, ActivityTimeLogSourceOption} from "./ActivityTimeLogForm";
import {WorkOutsideWorkingHoursBadge} from "./WorkOutsideWorkingHoursBadge";

interface ITeamViewerSessionMessage {
    userId: string;
    companySettingsId: number;
    companyId?: number;
    supportTicketId?: number;
    deviceId?: string;
    deviceName: string;
    teamViewerSessionId: string;
    startDate: Date;
    endDate: Date;
    duration?: string | number;
    notes: string;
    workOutsideWorkingHoursPreview?: boolean | null;
}

interface IWakaWorklogMessage {
    userId: string;
    companySettingsId: number;
    wakaTimeWorklogId: number;
    externalProjectName: string;
    date: Date;
    duration: string | number;
    sourceType: WakaTimeWorklogSourceType;
    summary?: string;
    commitCount: number;
}

interface IPantheonSessionMessage {
    userId: string;
    companySettingsId: number;
    pantheonSessionId: number;
    clientSessionId: string;
    pantheonIniId?: number;
    companyId?: number;
    supportTicketId?: number;
    pantheonCompanyName: string;
    status: string;
    startedAt: Date;
    endedAt: Date;
    duration: string | number;
    note?: string;
    workOutsideWorkingHoursPreview?: boolean | null;
}

interface ActivityGroup<T> {
    key: string;
    leader: T;
    members: T[];
    sourceKeys: string[];
}

const cardStyle: React.CSSProperties = {
    backgroundColor: "white",
    border: "1px solid #d6d6d6",
    borderRadius: 4,
    cursor: "pointer",
    minWidth: 240,
    maxWidth: 320,
    padding: 8,
};

const teamViewerSourceKey = (sessionId: string) => `teamviewer:${sessionId}`;
const wakaSourceKey = (worklogId: number) => `waka:${worklogId}`;
const pantheonSourceKey = (sessionId: number) => `pantheon:${sessionId}`;

const dateMs = (date: Date) => new Date(date as any).getTime();

const sessionCountLabel = (count: number) =>
    `${count} ${count === 1 ? "sesija" : count < 5 ? "sesije" : "sesija"}`;

const groupByOldest = <T,>(
    items: T[],
    groupKey: (item: T) => string,
    startedAt: (item: T) => Date,
    sourceKey: (item: T) => string,
): ActivityGroup<T>[] => {
    const groups = new Map<string, T[]>();

    items.forEach((item) => {
        const key = groupKey(item);
        groups.set(key, [...(groups.get(key) ?? []), item]);
    });

    return Array.from(groups.entries())
        .map(([key, members]) => {
            const sortedMembers = [...members].sort(
                (left, right) => dateMs(startedAt(left)) - dateMs(startedAt(right)),
            );

            return {
                key,
                leader: sortedMembers[0],
                members: sortedMembers,
                sourceKeys: sortedMembers.map(sourceKey),
            };
        })
        .sort((left, right) => dateMs(startedAt(left.leader)) - dateMs(startedAt(right.leader)));
};

const groupTeamViewerSessions = (sessions: ITeamViewerSessionMessage[]) =>
    groupByOldest(
        sessions,
        (session) =>
            session.deviceId?.trim() ||
            session.deviceName?.trim() ||
            session.teamViewerSessionId,
        (session) => session.startDate,
        (session) => teamViewerSourceKey(session.teamViewerSessionId),
    );

const groupPantheonSessions = (sessions: IPantheonSessionMessage[]) =>
    groupByOldest(
        sessions,
        (session) =>
            (session.pantheonIniId ? `ini:${session.pantheonIniId}` : "") ||
            (session.companyId ? `company:${session.companyId}` : "") ||
            session.pantheonCompanyName?.trim() ||
            session.clientSessionId ||
            String(session.pantheonSessionId),
        (session) => session.startedAt,
        (session) => pantheonSourceKey(session.pantheonSessionId),
    );

const formatDuration = (duration?: string | number) => {
    if (duration === undefined || duration === null) {
        return "00:00";
    }

    if (typeof duration === "string") {
        const parts = duration.split(":");
        if (parts.length >= 2) {
            return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
        }

        return duration;
    }

    let totalMinutes = duration;

    if (duration > 60 * 60 * 24) {
        totalMinutes = duration / 60;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const sourceTypeLabel = (sourceType: WakaTimeWorklogSourceType) => {
    switch (sourceType) {
        case WakaTimeWorklogSourceType.Commit:
            return "commit";
        case WakaTimeWorklogSourceType.Remainder:
            return "van commitova";
        case WakaTimeWorklogSourceType.Activity:
            return "bez commita";
        case WakaTimeWorklogSourceType.CodexTask:
            return "Codex";
        default:
            return "WakaTime";
    }
};

const commitContextLabel = (sourceType: WakaTimeWorklogSourceType, commitCount: number) => {
    if (sourceType === WakaTimeWorklogSourceType.CodexTask) {
        return "Codex zadatak";
    }

    if (commitCount <= 0) {
        return "bez commita";
    }

    return `${commitCount} commit${commitCount === 1 ? "" : "a"} u kontekstu`;
};

const pantheonStatusLabel = (status?: string) => {
    switch (status) {
        case "pantheonSessionTerminatedSuccessfully":
        case "pantheonSessionFinished":
            return "završeno";
        case "pantheonSessionTerminatedWithError":
        case "pantheonSessionError":
            return "greška";
        default:
            return "Pantheon";
    }
};

export const TeamViewerMessageBar = () => {
    const {pathname} = useLocation();
    const {serverDateToLocalDate, moment} = useContext(LanguageContext);
    const {UserInfo} = useContext(AuthContext);
    const {
        currentTicket,
        reloadCurrentTicketTeamViewerSessions,
        reloadCurrentTicketTimeLogsSessions,
    } = useContext(CurrentSupportTicketContext);
    const {bottomMessageBarHidden, setBottomMessageBarHidden} = useContext(
        TeamViewerMessageBarContext,
    );

    const [busyWakaWorklogId, setBusyWakaWorklogId] = useState<number>();
    const [busyTeamViewerSessionId, setBusyTeamViewerSessionId] = useState<string>();
    const [unassignedTeamViewerSessions, setUnassignedTeamViewerSessions] =
        useState<ITeamViewerSessionMessage[]>([]);
    const [unassignedWakaWorklogs, setUnassignedWakaWorklogs] =
        useState<IWakaWorklogMessage[]>([]);
    const [unassignedPantheonSessions, setUnassignedPantheonSessions] =
        useState<IPantheonSessionMessage[]>([]);
    const [busyPantheonSessionId, setBusyPantheonSessionId] = useState<number>();

    const isAtSupportPage = useMemo<boolean>(
        () => isCurrentSupportTicketRoute(pathname),
        [pathname],
    );
    const activeTicket = useMemo(
        () => getRouteScopedCurrentTicket(pathname, currentTicket),
        [currentTicket, pathname],
    );
    const roundingMinutes = useMemo(
        () => userTimeLogRoundingMinutes(UserInfo?.user as any),
        [UserInfo?.user],
    );

    const hasSupport = useMemo(
        () => UserInfoHelper.HasPacket(UserInfo, ApplicationPacketEnum.Support),
        [UserInfo],
    );

    const shortOrLongDate = useCallback(
        (dateValue: Date, forceShort = false) => {
            const date = serverDateToLocalDate(dateValue);
            if (forceShort || date.isSame(moment(), "day")) {
                return date.format("HH:mm");
            }

            return date.format("LLLL");
        },
        [moment, serverDateToLocalDate],
    );

    useEffect(() => {
        if (!hasSupport) {
            return;
        }

        const sessionsHub = MakeHub<ITeamViewerSessionMessage[]>(
            "UnreadSupportMessagesHub",
            "Sessions",
            (items) => setUnassignedTeamViewerSessions(items ?? []),
            undefined,
            true,
        );
        const wakaHub = MakeHub<IWakaWorklogMessage[]>(
            "UnreadSupportMessagesHub",
            "WakaWorklogs",
            (items) => setUnassignedWakaWorklogs(items ?? []),
            undefined,
            true,
        );
        const pantheonHub = MakeHub<IPantheonSessionMessage[]>(
            "UnreadSupportMessagesHub",
            "PantheonSessions",
            (items) => setUnassignedPantheonSessions(items ?? []),
            undefined,
            true,
        );

        return () => {
            sessionsHub.close();
            wakaHub.close();
            pantheonHub.close();
        };
    }, [hasSupport]);

    const dismissAllUnassigned = useCallback(() => {
        if (
            window.confirm(
                "Jeste li sigurni da želite sakriti sve nepovezane aktivnosti?",
            )
        ) {
            Axios.post("Support/RemoveUnassignedForever").catch(() => {
                alert(
                    "Došlo je do greške prilikom skrivanja aktivnosti. Pokušajte ponovo.",
                );
            });
        }
    }, []);

    const dismissWakaWorklog = useCallback(async (worklogId: number) => {
        setBusyWakaWorklogId(worklogId);

        try {
            await Axios.post(`WakaTimeWorklogs/${worklogId}/Dismiss`);
        } finally {
            setBusyWakaWorklogId(undefined);
        }
    }, []);

    const dismissTeamViewerSessions = useCallback(async (sessionIds: string[]) => {
        setBusyTeamViewerSessionId(sessionIds[0]);

        try {
            await Promise.all(
                sessionIds.map((sessionId) =>
                    Axios.post(`TeamViewerSessions/${sessionId}/Dismiss`),
                ),
            );
        } finally {
            setBusyTeamViewerSessionId(undefined);
        }
    }, []);

    const dismissPantheonSessions = useCallback(async (sessionIds: number[]) => {
        setBusyPantheonSessionId(sessionIds[0]);

        try {
            await Promise.all(
                sessionIds.map((sessionId) =>
                    Axios.post(`PantheonSessions/${sessionId}/Dismiss`),
                ),
            );
        } finally {
            setBusyPantheonSessionId(undefined);
        }
    }, []);

    const shouldDefaultNonBillable = useMemo(
        () =>
            !!(
                UserInfo?.user?.supportTimeLogsAlwaysNonBillable ||
                activeTicket?.offerTypeDefault
            ),
        [UserInfo?.user?.supportTimeLogsAlwaysNonBillable, activeTicket?.offerTypeDefault],
    );

    const baseBillableFields = useMemo(
        () => ({
            billable: !shouldDefaultNonBillable,
            invoice: !shouldDefaultNonBillable,
        }),
        [shouldDefaultNonBillable],
    );

    const billableTimeFromSuggestedTime = useCallback(
        (suggestedTime: string) =>
            baseBillableFields.billable || baseBillableFields.invoice
                ? roundTimeLogDurationUpToQuarterHour(suggestedTime)
                : "00:00",
        [baseBillableFields],
    );

    const visibleTeamViewerSessions = useMemo(
        () =>
            unassignedTeamViewerSessions.filter((session) =>
                isSupportActivityVisibleForTicket(activeTicket, session),
            ),
        [activeTicket, unassignedTeamViewerSessions],
    );

    const visiblePantheonSessions = useMemo(
        () =>
            unassignedPantheonSessions.filter((session) =>
                isSupportActivityVisibleForTicket(activeTicket, session),
            ),
        [activeTicket, unassignedPantheonSessions],
    );

    const visibleTeamViewerGroups = useMemo(
        () => groupTeamViewerSessions(visibleTeamViewerSessions),
        [visibleTeamViewerSessions],
    );

    const visiblePantheonGroups = useMemo(
        () => groupPantheonSessions(visiblePantheonSessions),
        [visiblePantheonSessions],
    );

    const hasPantheonSessionForActiveTicket = useMemo(
        () =>
            visiblePantheonSessions.some((session) =>
                isSupportActivityRelatedToCurrentTicket(activeTicket, session),
            ),
        [activeTicket, visiblePantheonSessions],
    );

    const hasTeamViewerSessionForActiveTicket = useMemo(
        () =>
            visibleTeamViewerSessions.some((session) =>
                isSupportActivityRelatedToCurrentTicket(activeTicket, session),
            ),
        [activeTicket, visibleTeamViewerSessions],
    );

    const teamViewerActivitySourceOption = useCallback(
        (session: ITeamViewerSessionMessage): ActivityTimeLogSourceOption => ({
            key: teamViewerSourceKey(session.teamViewerSessionId),
            type: "teamviewer",
            title: `TeamViewer: ${session.deviceName || "nepoznati uređaj"}`,
            subtitle: session.notes?.trim() || session.deviceId,
            timeLabel: `${formatDuration(session.duration)} · ${shortOrLongDate(session.startDate)} do ${shortOrLongDate(session.endDate, true)}`,
            duration: session.duration,
            startedAt: session.startDate,
            teamViewerSessionId: session.teamViewerSessionId,
            workOutsideWorkingHoursPreview: session.workOutsideWorkingHoursPreview,
        }),
        [shortOrLongDate],
    );

    const wakaActivitySourceOption = useCallback(
        (worklog: IWakaWorklogMessage): ActivityTimeLogSourceOption => ({
            key: wakaSourceKey(worklog.wakaTimeWorklogId),
            type: "waka",
            title: `WakaTime: ${worklog.externalProjectName}`,
            subtitle: worklog.summary?.trim() || commitContextLabel(worklog.sourceType, worklog.commitCount),
            timeLabel: `${formatDuration(worklog.duration)} · ${sourceTypeLabel(worklog.sourceType)}`,
            duration: worklog.duration,
            startedAt: worklog.date,
            wakaTimeWorklogId: worklog.wakaTimeWorklogId,
        }),
        [],
    );

    const pantheonActivitySourceOption = useCallback(
        (session: IPantheonSessionMessage): ActivityTimeLogSourceOption => ({
            key: pantheonSourceKey(session.pantheonSessionId),
            type: "pantheon",
            title: `Pantheon: ${session.pantheonCompanyName || "Pantheon"}`,
            subtitle: session.note?.trim() || pantheonStatusLabel(session.status),
            timeLabel: `${formatDuration(session.duration)} · ${shortOrLongDate(session.startedAt)} do ${shortOrLongDate(session.endedAt, true)}`,
            duration: session.duration,
            startedAt: session.startedAt,
            pantheonSessionId: session.pantheonSessionId,
            workOutsideWorkingHoursPreview: session.workOutsideWorkingHoursPreview,
        }),
        [shortOrLongDate],
    );

    const allActivitySourceOptions = useMemo(
        () => [
            ...visibleTeamViewerSessions.map(teamViewerActivitySourceOption),
            ...unassignedWakaWorklogs.map(wakaActivitySourceOption),
            ...visiblePantheonSessions.map(pantheonActivitySourceOption),
        ],
        [
            pantheonActivitySourceOption,
            teamViewerActivitySourceOption,
            unassignedWakaWorklogs,
            visiblePantheonSessions,
            visibleTeamViewerSessions,
            wakaActivitySourceOption,
        ],
    );

    const teamViewerTimeLogBaseDto = useCallback(
        (group: ActivityGroup<ITeamViewerSessionMessage>) => {
            const session = group.leader;
            const totalMinutes = group.members.reduce(
                (sum, item) => sum + parseTimeLogDurationToMinutes(item.duration),
                0,
            );
            const suggestedTime = suggestTimeLogDuration(totalMinutes, roundingMinutes);
            const note =
                "TeamViewer podrška: " +
                (session.notes?.trim() || session.deviceName || "") +
                (group.members.length > 1 ? ` (${sessionCountLabel(group.members.length)})` : "");

            return {
                ticketId: activeTicket?.id ?? session.supportTicketId,
                ticketIdstorage: activeTicket,
                teamViewerSessionId: session.teamViewerSessionId,
                teamViewerSessionIds: group.members.map((item) => item.teamViewerSessionId),
                time: suggestedTime,
                billableTime: billableTimeFromSuggestedTime(suggestedTime),
                date: session.startDate,
                type: "TeamViewer",
                note,
                workOutsideWorkingHoursPreview: group.members.some((item) => item.workOutsideWorkingHoursPreview === true),
                workOutsideWorkingHours: group.members.some((item) => item.workOutsideWorkingHoursPreview === true),
                ...baseBillableFields,
            } as any;
        },
        [activeTicket, baseBillableFields, billableTimeFromSuggestedTime, roundingMinutes],
    );

    const wakaTimeLogBaseDto = useCallback(
        (worklog: IWakaWorklogMessage) => {
            const suggestedTime = suggestTimeLogDuration(worklog.duration, roundingMinutes);
            const notePrefix =
                worklog.sourceType === WakaTimeWorklogSourceType.CodexTask ? "Codex" : "WakaTime";
            const note = worklog.summary?.trim()
                ? `${notePrefix}: ${worklog.externalProjectName} - ${worklog.summary.trim()}`
                : `${notePrefix}: ${worklog.externalProjectName}`;

            return {
                ticketId: activeTicket?.id,
                ticketIdstorage: activeTicket,
                wakaTimeWorklogId: worklog.wakaTimeWorklogId,
                time: suggestedTime,
                billableTime: billableTimeFromSuggestedTime(suggestedTime),
                date: worklog.date,
                type: `WakaTime ${sourceTypeLabel(worklog.sourceType)}`,
                note,
                ...baseBillableFields,
            } as any;
        },
        [activeTicket, baseBillableFields, billableTimeFromSuggestedTime, roundingMinutes],
    );

    const pantheonTimeLogBaseDto = useCallback(
        (group: ActivityGroup<IPantheonSessionMessage>) => {
            const session = group.leader;
            const totalMinutes = group.members.reduce(
                (sum, item) => sum + parseTimeLogDurationToMinutes(item.duration),
                0,
            );
            const suggestedTime = suggestTimeLogDuration(totalMinutes, roundingMinutes);
            const note =
                session.note?.trim() ||
                `Pantheon sesija: ${session.pantheonCompanyName ?? "Pantheon"}`;

            return ({
                ticketId: activeTicket?.id ?? session.supportTicketId,
                ticketIdstorage: activeTicket,
                pantheonSessionId: session.pantheonSessionId,
                pantheonSessionIds: group.members.map((item) => item.pantheonSessionId),
                time: suggestedTime,
                billableTime: billableTimeFromSuggestedTime(suggestedTime),
                date: session.startedAt,
                type: "Pantheon sesija",
                note: note + (group.members.length > 1 ? ` (${sessionCountLabel(group.members.length)})` : ""),
                workOutsideWorkingHoursPreview: group.members.some((item) => item.workOutsideWorkingHoursPreview === true),
                workOutsideWorkingHours: group.members.some((item) => item.workOutsideWorkingHoursPreview === true),
                ...baseBillableFields,
            } as any);
        },
        [activeTicket, baseBillableFields, billableTimeFromSuggestedTime, roundingMinutes],
    );

    const afterActivityTimeLogAction = useCallback((sourceKeys: string[]) => {
        const keySet = new Set(sourceKeys);
        setUnassignedTeamViewerSessions((items) =>
            items.filter((item) => !keySet.has(teamViewerSourceKey(item.teamViewerSessionId))),
        );
        setUnassignedWakaWorklogs((items) =>
            items.filter((item) => !keySet.has(wakaSourceKey(item.wakaTimeWorklogId))),
        );
        setUnassignedPantheonSessions((items) =>
            items.filter((item) => !keySet.has(pantheonSourceKey(item.pantheonSessionId))),
        );
        reloadCurrentTicketTeamViewerSessions?.();
        reloadCurrentTicketTimeLogsSessions?.();
    }, [reloadCurrentTicketTeamViewerSessions, reloadCurrentTicketTimeLogsSessions]);

    const hasVisibleItems =
        !!visibleTeamViewerGroups?.length ||
        !!unassignedWakaWorklogs?.length ||
        !!visiblePantheonGroups?.length;

    if (!hasSupport || bottomMessageBarHidden || !hasVisibleItems) {
        return null;
    }

    return (
        <MessageBar
            styles={{
                root: {position: "fixed", bottom: 0, zIndex: 100, width: "100%"},
            }}
            actions={
                <>
                    <MessageBarButton
                        text="Privremeno sakrij"
                        onClick={() => setBottomMessageBarHidden(true)}
                    />
                    <MessageBarButton
                        text="Sakrij sve nepovezane aktivnosti"
                        style={{backgroundColor: SharedColors.red10, color: "white"}}
                        onClick={dismissAllUnassigned}
                    />
                </>
            }
            messageBarType={
                isAtSupportPage ? MessageBarType.success : MessageBarType.severeWarning
            }
        >
            <span>
                {hasTeamViewerSessionForActiveTicket && hasPantheonSessionForActiveTicket
                    ? "Imate TeamViewer i Pantheon sesiju koje su možda vezane uz ovaj tiket."
                    : hasTeamViewerSessionForActiveTicket
                    ? "Imate TeamViewer sesiju koja je možda vezana uz ovaj tiket."
                    : hasPantheonSessionForActiveTicket
                    ? "Imate Pantheon sesiju koja je možda vezana uz ovaj tiket."
                    : isAtSupportPage
                    ? "Imate nepovezanih TeamViewer sesija, WakaTime rada ili Pantheon sesija koje mozete povezati s ovim tiketom."
                    : "Imate nepovezanih TeamViewer sesija, WakaTime rada ili Pantheon sesija."}
            </span>
            <Stack
                tokens={{childrenGap: 8}}
                style={{paddingTop: 8}}
                grow
                styles={{inner: {width: "100%"}, root: {width: "100%"}}}
            >
                {!!visiblePantheonGroups?.length && (
                    <Stack tokens={{childrenGap: 6}}>
                        <Stack horizontal verticalAlign="center" tokens={{childrenGap: 8}}>
                            <Icon
                                iconName="Database"
                                styles={{
                                    root: {
                                        fontSize: 32,
                                        color: "#6b8e23",
                                    },
                                }}
                            />
                            <Text variant="mediumPlus">Pantheon sesije</Text>
                        </Stack>
                        <Stack
                            horizontal
                            wrap
                            horizontalAlign="start"
                            tokens={{childrenGap: 8}}
                            verticalAlign="stretch"
                        >
                            {visiblePantheonGroups.map((group) => {
                                const session = group.leader;
                                const isBusy =
                                    group.members.some((item) =>
                                        busyPantheonSessionId === item.pantheonSessionId,
                                    );
                                const totalDuration = group.members.reduce(
                                    (sum, item) => sum + parseTimeLogDurationToMinutes(item.duration),
                                    0,
                                );
                                const candidateLabel = supportActivityCandidateLabel(
                                    activeTicket,
                                    session,
                                );
                                const isRelatedToActiveTicket =
                                    isSupportActivityRelatedToCurrentTicket(
                                        activeTicket,
                                        session,
                                    );

                                return (
                                    <OnixCallout
                                        key={`pantheon-session-${group.key}`}
                                        heightless
                                        title={"Izradi vrijeme rada" as any}
                                        trigger={(id, show) => (
                                            <div
                                                id={id}
                                                className="has-clickable-action-default"
                                                style={cardStyle}
                                                onClick={
                                                    isBusy
                                                        ? undefined
                                                        : (event) => show(event, undefined)
                                                }
                                            >
                                                <Stack tokens={{childrenGap: 6}}>
                                                    <Stack
                                                        horizontal
                                                        verticalAlign="start"
                                                        horizontalAlign="space-between"
                                                    >
                                                        <Stack styles={{root: {paddingRight: 8}}}>
                                                            <Text variant="mediumPlus">
                                                                {session.pantheonCompanyName ||
                                                                    "Pantheon"}
                                                            </Text>
                                                            <Text variant="small">
                                                                {formatDuration(totalDuration)} ·{" "}
                                                                {pantheonStatusLabel(
                                                                    session.status,
                                                                )}{" "}
                                                                ·{" "}
                                                                {serverDateToLocalDate(
                                                                    session.endedAt,
                                                                ).fromNow()}
                                                            </Text>
                                                        </Stack>
                                                        <IconButton
                                                            iconProps={{iconName: "Clear"}}
                                                            title="Sakrij"
                                                            ariaLabel="Sakrij"
                                                            disabled={isBusy}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                dismissPantheonSessions(
                                                                    group.members.map((item) =>
                                                                        item.pantheonSessionId,
                                                                    ),
                                                                );
                                                            }}
                                                        />
                                                    </Stack>
                                                    <Text>
                                                        {session.note?.trim() ||
                                                            `Pantheon sesija: ${
                                                                session.pantheonCompanyName ??
                                                                "Pantheon"
                                                            }`}
                                                    </Text>
                                                    {candidateLabel ? (
                                                        <Text
                                                            variant="small"
                                                            styles={{
                                                                root: {
                                                                    alignSelf: "flex-start",
                                                                    backgroundColor:
                                                                        isRelatedToActiveTicket
                                                                            ? "#e5f4e3"
                                                                            : "#fff4ce",
                                                                    border:
                                                                        "1px solid #d0d0d0",
                                                                    borderRadius: 4,
                                                                    color:
                                                                        isRelatedToActiveTicket
                                                                            ? "#107c10"
                                                                            : "#605e5c",
                                                                    fontWeight: 600,
                                                                    padding: "2px 6px",
                                                                },
                                                            }}
                                                        >
                                                            {candidateLabel}
                                                        </Text>
                                                    ) : null}
                                                    {group.members.some((item) => item.workOutsideWorkingHoursPreview === true) ? (
                                                        <WorkOutsideWorkingHoursBadge compact/>
                                                    ) : null}
                                                    {group.members.length > 1 ? (
                                                        <Text
                                                            variant="small"
                                                            styles={{
                                                                root: {
                                                                    alignSelf: "flex-start",
                                                                    backgroundColor: "#f3f2f1",
                                                                    border: "1px solid #d0d0d0",
                                                                    borderRadius: 4,
                                                                    color: "#323130",
                                                                    fontWeight: 600,
                                                                    padding: "2px 6px",
                                                                },
                                                            }}
                                                        >
                                                            Grupirano: {sessionCountLabel(group.members.length)}
                                                        </Text>
                                                    ) : null}
                                                    <Text variant="small">
                                                        {shortOrLongDate(
                                                            session.startedAt,
                                                        )}{" "}
                                                        do{" "}
                                                        {shortOrLongDate(
                                                            session.endedAt,
                                                            true,
                                                        )}
                                                    </Text>
                                                    {isBusy ? <ProgressIndicator/> : null}
                                                </Stack>
                                            </div>
                                        )}
                                    >
                                        {() => (
                                            <ActivityTimeLogForm
                                                key={`pantheon-time-log-${group.key}-${group.sourceKeys.join("-")}`}
                                                baseDto={pantheonTimeLogBaseDto(group)}
                                                currentTicket={activeTicket as any}
                                                user={UserInfo?.user as any}
                                                defaultMode={
                                                    activeTicket?.id || session.supportTicketId
                                                        ? "existing"
                                                        : "new"
                                                }
                                                defaultTicket={{
                                                    title:
                                                        session.note?.trim() ||
                                                        `Pantheon sesija: ${
                                                            session.pantheonCompanyName ??
                                                            "Pantheon"
                                                        }`,
                                                    companyId: session.companyId,
                                                    companyName: session.pantheonCompanyName,
                                                }}
                                                activitySourceOptions={allActivitySourceOptions}
                                                defaultActivitySourceKeys={group.sourceKeys}
                                                primaryActivitySourceKey={pantheonSourceKey(session.pantheonSessionId)}
                                                activitySourceRoundingMinutes={roundingMinutes}
                                                onSaved={(result) =>
                                                    afterActivityTimeLogAction(
                                                        result.activitySourceKeys ?? group.sourceKeys,
                                                    )
                                                }
                                            />
                                        )}
                                    </OnixCallout>
                                );
                            })}
                        </Stack>
                    </Stack>
                )}
                {!!visiblePantheonGroups?.length &&
                (!!unassignedWakaWorklogs?.length ||
                    !!visibleTeamViewerGroups?.length) ? (
                    <hr
                        style={{
                            padding: 1,
                            backgroundColor: "#bbbbbb",
                            width: "100%",
                            border: "none",
                        }}
                    />
                ) : null}
                {!!unassignedWakaWorklogs?.length && (
                    <Stack tokens={{childrenGap: 6}}>
                        <Stack horizontal verticalAlign="center" tokens={{childrenGap: 8}}>
                            <Icon
                                iconName="Code"
                                styles={{
                                    root: {
                                        fontSize: 32,
                                        color: "#1264a3",
                                    },
                                }}
                            />
                            <Text variant="mediumPlus">WakaTime</Text>
                        </Stack>
                        <Stack
                            horizontal
                            wrap
                            horizontalAlign="start"
                            tokens={{childrenGap: 8}}
                            verticalAlign="stretch"
                        >
                            {unassignedWakaWorklogs.map((worklog) => {
                                const isBusy =
                                    busyWakaWorklogId === worklog.wakaTimeWorklogId;

                                return (
                                    <OnixCallout
                                        key={`waka-worklog-${worklog.wakaTimeWorklogId}`}
                                        heightless
                                        title={"Izradi vrijeme rada" as any}
                                        trigger={(id, show) => (
                                            <div
                                                id={id}
                                                className="has-clickable-action-default"
                                                style={cardStyle}
                                                onClick={
                                                    isBusy
                                                        ? undefined
                                                        : (event) => show(event, undefined)
                                                }
                                            >
                                                <Stack tokens={{childrenGap: 6}}>
                                                    <Stack
                                                        horizontal
                                                        verticalAlign="start"
                                                        horizontalAlign="space-between"
                                                    >
                                                        <Stack styles={{root: {paddingRight: 8}}}>
                                                            <Text variant="mediumPlus">
                                                                {worklog.externalProjectName}
                                                            </Text>
                                                            <Text variant="small">
                                                                {formatDuration(worklog.duration)} ·{" "}
                                                                {sourceTypeLabel(worklog.sourceType)} ·{" "}
                                                                {serverDateToLocalDate(
                                                                    worklog.date,
                                                                ).fromNow()}
                                                            </Text>
                                                        </Stack>
                                                        <IconButton
                                                            iconProps={{iconName: "Clear"}}
                                                            title="Sakrij"
                                                            ariaLabel="Sakrij"
                                                            disabled={isBusy}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                dismissWakaWorklog(
                                                                    worklog.wakaTimeWorklogId,
                                                                );
                                                            }}
                                                        />
                                                    </Stack>
                                                    <Text>
                                                        {worklog.summary?.trim() ||
                                                            `${worklog.commitCount} commit${
                                                                worklog.commitCount === 1 ? "" : "a"
                                                            }`}
                                                    </Text>
                                                    <Text variant="small">
                                                        {shortOrLongDate(worklog.date)} ·{" "}
                                                        {commitContextLabel(
                                                            worklog.sourceType,
                                                            worklog.commitCount,
                                                        )}
                                                    </Text>
                                                    {isBusy ? <ProgressIndicator/> : null}
                                                </Stack>
                                            </div>
                                        )}
                                    >
                                        {() => (
                                            <ActivityTimeLogForm
                                                key={`waka-time-log-${worklog.wakaTimeWorklogId}`}
                                                baseDto={wakaTimeLogBaseDto(worklog)}
                                                currentTicket={activeTicket as any}
                                                user={UserInfo?.user as any}
                                                defaultMode={activeTicket?.id ? "existing" : "new"}
                                                defaultTicket={{
                                                    title: worklog.summary?.trim()
                                                        ? `WakaTime: ${worklog.externalProjectName} - ${worklog.summary.trim()}`
                                                        : `WakaTime: ${worklog.externalProjectName}`,
                                                }}
                                                activitySourceOptions={allActivitySourceOptions}
                                                defaultActivitySourceKeys={[wakaSourceKey(worklog.wakaTimeWorklogId)]}
                                                primaryActivitySourceKey={wakaSourceKey(worklog.wakaTimeWorklogId)}
                                                activitySourceRoundingMinutes={roundingMinutes}
                                                onSaved={(result) =>
                                                    afterActivityTimeLogAction(
                                                        result.activitySourceKeys ?? [wakaSourceKey(worklog.wakaTimeWorklogId)],
                                                    )
                                                }
                                            />
                                        )}
                                    </OnixCallout>
                                );
                            })}
                        </Stack>
                    </Stack>
                )}
                {!!unassignedWakaWorklogs?.length &&
                !!visibleTeamViewerGroups?.length ? (
                    <hr
                        style={{
                            padding: 1,
                            backgroundColor: "#bbbbbb",
                            width: "100%",
                            border: "none",
                        }}
                    />
                ) : null}
                {!!visibleTeamViewerGroups?.length && (
                    <Stack tokens={{childrenGap: 6}}>
                        <Stack horizontal verticalAlign="center" tokens={{childrenGap: 8}}>
                            <Icon
                                iconName="Remote"
                                styles={{
                                    root: {
                                        fontSize: 32,
                                        color: "#00a1ff",
                                    },
                                }}
                            />
                            <Text variant="mediumPlus">TeamViewer</Text>
                        </Stack>
                        <Stack
                            wrap
                            horizontal
                            horizontalAlign="start"
                            tokens={{childrenGap: 8}}
                            verticalAlign="center"
                        >
                            {visibleTeamViewerGroups.map((group) => {
                                const session = group.leader;
                                const isBusy =
                                    group.members.some((item) =>
                                        busyTeamViewerSessionId === item.teamViewerSessionId,
                                    );
                                const candidateLabel = supportActivityCandidateLabel(
                                    activeTicket,
                                    session,
                                );
                                const isRelatedToActiveTicket =
                                    isSupportActivityRelatedToCurrentTicket(
                                        activeTicket,
                                        session,
                                    );

                                return (
                                    <OnixCallout
                                        key={`teamviewer-session-${group.key}`}
                                        heightless
                                        title={"Izradi vrijeme rada" as any}
                                        trigger={(id, show) => (
                                            <div
                                                id={id}
                                                className="has-clickable-action-default"
                                                style={{...cardStyle, minWidth: 260}}
                                                onClick={
                                                    isBusy
                                                        ? undefined
                                                        : (event) => show(event, undefined)
                                                }
                                            >
                                                <Stack tokens={{childrenGap: 6}}>
                                                    <Stack
                                                        horizontal
                                                        verticalAlign="start"
                                                        horizontalAlign="space-between"
                                                    >
                                                        <Stack.Item grow>
                                                            <ActivityItem
                                                                activityPersonas={[
                                                                    {
                                                                        imageInitials: getInitials(
                                                                            session.deviceName ??
                                                                            "Nepoznati uredaj",
                                                                            false,
                                                                            true,
                                                                        ),
                                                                        text:
                                                                            session.deviceName ??
                                                                            "Nepoznati uredaj",
                                                                    },
                                                                ]}
                                                                activityDescription={
                                                                    session.deviceName ??
                                                                    " - nepoznati uredaj - "
                                                                }
                                                                comments={[session.notes]}
                                                                timeStamp={
                                                                    <div>
                                                                        <b>
                                                                            {shortOrLongDate(
                                                                                session.startDate,
                                                                            )}
                                                                        </b>{" "}
                                                                        do{" "}
                                                                        <b>
                                                                            {shortOrLongDate(
                                                                                session.endDate,
                                                                                true,
                                                                            )}
                                                                        </b>{" "}
                                                                        (
                                                                        {serverDateToLocalDate(
                                                                            session.endDate,
                                                                        ).fromNow()}
                                                                        )
                                                                    </div>
                                                                }
                                                            />
                                                        </Stack.Item>
                                                        <IconButton
                                                            iconProps={{iconName: "Clear"}}
                                                            title="Sakrij"
                                                            ariaLabel="Sakrij"
                                                            disabled={isBusy}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                dismissTeamViewerSessions(
                                                                    group.members.map((item) =>
                                                                        item.teamViewerSessionId,
                                                                    ),
                                                                );
                                                            }}
                                                        />
                                                    </Stack>
                                                    {group.members.some((item) => item.workOutsideWorkingHoursPreview === true) ? (
                                                        <WorkOutsideWorkingHoursBadge compact/>
                                                    ) : null}
                                                    {group.members.length > 1 ? (
                                                        <Text
                                                            variant="small"
                                                            styles={{
                                                                root: {
                                                                    alignSelf: "flex-start",
                                                                    backgroundColor: "#f3f2f1",
                                                                    border: "1px solid #d0d0d0",
                                                                    borderRadius: 4,
                                                                    color: "#323130",
                                                                    fontWeight: 600,
                                                                    padding: "2px 6px",
                                                                },
                                                            }}
                                                        >
                                                            Grupirano: {sessionCountLabel(group.members.length)}
                                                        </Text>
                                                    ) : null}
                                                    {candidateLabel ? (
                                                        <Text
                                                            variant="small"
                                                            styles={{
                                                                root: {
                                                                    alignSelf: "flex-start",
                                                                    backgroundColor:
                                                                        isRelatedToActiveTicket
                                                                            ? "#e5f4e3"
                                                                            : "#fff4ce",
                                                                    border:
                                                                        "1px solid #d0d0d0",
                                                                    borderRadius: 4,
                                                                    color:
                                                                        isRelatedToActiveTicket
                                                                            ? "#107c10"
                                                                            : "#605e5c",
                                                                    fontWeight: 600,
                                                                    padding: "2px 6px",
                                                                },
                                                            }}
                                                        >
                                                            {candidateLabel}
                                                        </Text>
                                                    ) : null}
                                                    {isBusy ? <ProgressIndicator/> : null}
                                                </Stack>
                                            </div>
                                        )}
                                    >
                                        {() => (
                                            <ActivityTimeLogForm
                                                key={`teamviewer-time-log-${group.key}-${group.sourceKeys.join("-")}`}
                                                baseDto={teamViewerTimeLogBaseDto(group)}
                                                currentTicket={activeTicket as any}
                                                user={UserInfo?.user as any}
                                                defaultMode={
                                                    activeTicket?.id || session.supportTicketId
                                                        ? "existing"
                                                        : "new"
                                                }
                                                teamViewerDeviceIdForContactLookup={session.deviceId}
                                                defaultTicket={{
                                                    title: "TeamViewer podrška: " +
                                                        (session.deviceName ||
                                                            "nepoznati uređaj"),
                                                    companyId: session.companyId,
                                                }}
                                                activitySourceOptions={allActivitySourceOptions}
                                                defaultActivitySourceKeys={group.sourceKeys}
                                                primaryActivitySourceKey={teamViewerSourceKey(session.teamViewerSessionId)}
                                                activitySourceRoundingMinutes={roundingMinutes}
                                                onSaved={(result) =>
                                                    afterActivityTimeLogAction(
                                                        result.activitySourceKeys ?? group.sourceKeys,
                                                    )
                                                }
                                            />
                                        )}
                                    </OnixCallout>
                                );
                            })}
                        </Stack>
                    </Stack>
                )}
            </Stack>
        </MessageBar>
    );
};
